import {
    AwesomeTableComponent,
    Button,
    DialogManager,
    IColumnsProps,
    Icon,
    InputTextSearch,
    Notifications,
    ObjectUtils,
    Progress,
    useFirstTime,
} from "d-react-components";
import {
    debounce,
    filter,
    find,
    includes,
    isEmpty,
    map,
    toLower,
    uniqBy,
} from "lodash";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import ProductAPI from "../../../api/queries/product";
import TreatmentAPI from "../../../api/queries/treatment";
import Drawer from "../../../common/Drawer";
import { TreatmentDetailContext } from "../../../context/treatment";
import { IProduct } from "../../../interfaces/product";
import { ITreatmentProduct } from "../../../interfaces/treatment";
import Messages from "../../../languages/Messages";
import ProductNameView from "../../product/share/ProductNameView";
import QuantityInputField from "../../quotation/share/QuantityInputField";
import { TypeOfProduct } from "../../../api/hooks";
import { TREATMENT_STATUS } from "../../../constant/treatment";

interface IServiceItem extends IProduct {
    quantity: number;
    provideAfterTreatment: boolean;
}

interface ITreatmentAddonAddDrawer {
    open: boolean;
    onClose: () => void;
    defaultValue: IServiceItem[];
}

interface ITreatmentAddonUpdateDrawer {
    open: boolean;
    onClose: () => void;
    defaultValue: IServiceItem;
}

const FILTER_TABS = [
    {
        id: "all",
        label: "all",
    },
    {
        id: "selected",
        label: "selected",
    },
];

const TreatmentAddonAddDrawer = ({
    open,
    onClose,
    defaultValue = [],
}: ITreatmentAddonAddDrawer) => {
    const { treatment, setTreatment } = useContext(TreatmentDetailContext);
    const [addonSelected, setAddonSelected] =
        useState<IServiceItem[]>(defaultValue);
    const [filterTab, setFilterTab] = useState("all");
    const searchRef = useRef<string>();
    const tableRef = useRef<any>();
    const isFirstTime = useFirstTime();

    useEffect(() => {
        if (isFirstTime) return;
        tableRef.current.refresh();
    }, [filterTab]);

    const onAddAddonTreatment = () => {
        // const addonFiltered = filter(addonSelected, (item) => !!item.quantity);
        const products = map(addonSelected, (item) => ({
            product: item.id,
            quantity: item.quantity,
            provideAfterTreatment: !!item.provideAfterTreatment,
        }));

        Progress.show(
            {
                method: TreatmentAPI.addAddonService,
                params: [treatment?.id, products],
            },
            (treatmentRes: any) => {
                Notifications.showSuccess(Messages.addSuccess);
                setTreatment(treatmentRes);
                onClose();
            }
        );
    };

    const onChangeSearch = debounce((event) => {
        const search = event.target.value;
        searchRef.current = search;
        tableRef.current.refresh();
    }, 400);

    const columns: IColumnsProps = [
        {
            title: Messages.item,
            dataIndex: "",
            render: (product) => <ProductNameView product={product} />,
        },
        {
            title: Messages.unit,
            dataIndex: "unit",
        },
        {
            title: Messages.quantity,
            dataIndex: "",
            render: (product: any) => {
                const productSelected = find(
                    addonSelected,
                    (item) => item.id === product.id
                );
                if (!productSelected) return <div />;
                return (
                    <QuantityInputField
                        quantity={productSelected?.quantity}
                        onChangeQuantity={(quantity: number) =>
                            onChangeQuantity(quantity, product)
                        }
                        min={1}
                        disabled={productSelected?.provideAfterTreatment}
                    />
                );
            },
        },
    ];

    const onChangeQuantity = (quantity: number, product: IProduct) => {
        const result = ObjectUtils.updateArrayById(addonSelected as any, {
            ...product,
            quantity,
        });
        setAddonSelected(result);
    };

    const source = (pagingData: any) => {
        if (filterTab === "selected") {
            return Promise.resolve({ data: addonSelected });
        }
        return ProductAPI.list({
            page: pagingData?.pageIndex as any,
            limit: pagingData?.pageSize,
            search: searchRef?.current ?? "",
            isOperationService: false,
            typeOfProduct: [TypeOfProduct.ServiceProduct],
            sort: {
                createdAt: -1,
            },
        });
    };

    const onChangeSelectProduct = (keys: string[], products: IProduct[]) => {
        const allProducts = filter(
            [...addonSelected, ...products],
            (item) => !!item
        ).map((item: any) => ({ ...item, quantity: item?.quantity ?? 1 }));

        const result = map(keys, (id) =>
            find(allProducts, (item) => item.id === id)
        ).filter((item) => !!item);

        setAddonSelected(result);
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={Messages.service}
            onSave={onAddAddonTreatment}
            size="auto"
            width="80%"
        >
            <div className="p-4">
                <InputTextSearch onChange={onChangeSearch} />
                <div className="d-flex mt-3">
                    {map(FILTER_TABS, (item) => (
                        <Button
                            onClick={() => setFilterTab(item?.id)}
                            variant={
                                item.id === filterTab ? "standard" : "outline"
                            }
                            className="mr-2"
                            size="small"
                        >
                            {Messages[item.label]}
                        </Button>
                    ))}
                </div>
                <AwesomeTableComponent
                    className="mt-3"
                    ref={(ref) => {
                        tableRef.current = ref;
                    }}
                    columns={columns}
                    source={source}
                    getTotalItems={(res) => {
                        return res?.pagination?.items ?? 0;
                    }}
                    transformer={(res) => {
                        return res?.data ?? [];
                    }}
                    rowSelection={{
                        selectedRowKeys: map(addonSelected, (item) => item.id),
                        preserveSelectedRowKeys: true,
                        onChange: onChangeSelectProduct as any,
                    }}
                />
            </div>
        </Drawer>
    );
};

const TreatmentAddonUpdateDrawer = ({
    open,
    onClose,
    defaultValue,
}: ITreatmentAddonUpdateDrawer) => {
    const { treatment, setTreatment } = useContext(TreatmentDetailContext);
    const [service, setService] = useState<IServiceItem>(defaultValue);

    const onUpdateAddonServiceTreatment = () => {
        const input = { product: service?.id, quantity: service?.quantity };
        Progress.show(
            {
                method: TreatmentAPI.updateAddonService,
                params: [treatment?.id, input],
            },
            (treatmentRes: any) => {
                Notifications.showSuccess(Messages.updateSuccess);
                setTreatment(treatmentRes);
                onClose();
            }
        );
    };

    const columns: IColumnsProps = [
        {
            title: Messages.item,
            dataIndex: "",
            render: (product) => <ProductNameView product={product} />,
        },
        {
            title: Messages.unit,
            dataIndex: "unit",
        },
        {
            title: Messages.quantity,
            dataIndex: "quantity",
            render: (quantity: any) => {
                return (
                    <QuantityInputField
                        quantity={quantity}
                        onChangeQuantity={(quantity: number) =>
                            setService({ ...service, quantity })
                        }
                        min={1}
                    />
                );
            },
        },
    ];

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={Messages.addonServices}
            onSave={onUpdateAddonServiceTreatment}
            size="auto"
            width="80%"
        >
            <div className="p-4">
                <AwesomeTableComponent
                    className="mt-3"
                    columns={columns}
                    dataSource={[service]}
                    pagination={false}
                />
            </div>
        </Drawer>
    );
};

const TreatmentDetailAddonServices = () => {
    const { treatment, setTreatment } = useContext(TreatmentDetailContext);
    const [openAdd, setOpenAdd] = useState(false);
    const [updatingService, setupdatingService] = useState<IServiceItem | null>(
        null
    );
    const [textSearch, setTextSearch] = useState("");

    const getEditable = (service: any) => {
        if (treatment?.doctorOrder?.status === "COMPLETED") {
            return false;
        }

        if (service?.isPreset) {
            return true;
        }

        if (service?.hasPermissionAdjustment) {
            return treatment?.status === TREATMENT_STATUS.COMPLETED;
        }

        return service?.hasPermissionAdjustment;
    };

    const columns: IColumnsProps = [
        {
            title: Messages.item,
            dataIndex: "product",
            render: (product: IProduct) => (
                <ProductNameView product={product} />
            ),
        },
        {
            title: Messages.unit,
            dataIndex: "product",
            render: (product: IProduct) => product?.unit,
        },
        {
            title: Messages.quantity,
            dataIndex: "quantity",
            render: (quantity, item) =>
                item.provideAfterTreatment
                    ? Messages.provideAfterTreatment
                    : quantity,
        },
        {
            title: Messages.action,
            dataIndex: "",
            render: (service) => {
                const editable = getEditable(service);
                const deletable =
                    treatment?.doctorOrder?.status !== "COMPLETED" &&
                    service.hasPermissionDelete;

                return (
                    <div className="d-flex">
                        <Button
                            variant="trans"
                            iconName="edit"
                            className={classNames(
                                "cursor-pointer text-primary",
                                {
                                    "text-disabled": !editable,
                                }
                            )}
                            onClick={() =>
                                setupdatingService({
                                    ...service.product,
                                    quantity: service?.quantity,
                                })
                            }
                            disabled={!editable}
                        />
                        <Button
                            variant="trans"
                            iconName="remove_circle_outline"
                            className={classNames(
                                "cursor-pointer text-primary",
                                {
                                    "text-disabled": !deletable,
                                }
                            )}
                            onClick={() => onRemove(service)}
                            disabled={!deletable}
                        />
                    </div>
                );
            },
        },
    ];

    const onRemove = (service: ITreatmentProduct) => {
        DialogManager.showConfirm(
            Messages.confirm,
            Messages.areYouSureWantRemove,
            () =>
                Progress.show(
                    {
                        method: TreatmentAPI.removeAddonService,
                        params: [treatment?.id, service.product?.id],
                    },
                    (treatmentRes) => {
                        setTreatment(treatmentRes);
                        Notifications.showSuccess(Messages.removeSuccess);
                    }
                )
        );
    };

    const onChangeSearch = debounce((event) => {
        const search = event.target.value;
        setTextSearch(search);
    }, 400);

    const dataSource = useMemo(() => {
        if (isEmpty(textSearch)) {
            return treatment?.addOnServices ?? [];
        }

        return filter(treatment?.addOnServices ?? [], (user) =>
            includes(
                toLower(user?.product?.name + user?.product?.sku),
                toLower(textSearch)
            )
        );
    }, [textSearch, treatment?.addOnServices]);

    return (
        <div className="mt-3">
            <div className="flex-row-between-center">
                <label>{Messages.addonServices}</label>
                <Button
                    onClick={() => setOpenAdd(true)}
                    className=""
                    disabled={treatment?.doctorOrder?.status === "COMPLETED"}
                >
                    {Messages.add}
                </Button>
            </div>
            <InputTextSearch onChange={onChangeSearch} className="mt-3" />
            <AwesomeTableComponent
                columns={columns}
                dataSource={dataSource}
                className="mt-3"
                pagination={false}
            />
            {openAdd && (
                <TreatmentAddonAddDrawer
                    open={openAdd}
                    onClose={() => setOpenAdd(false)}
                    defaultValue={map(treatment?.addOnServices, (item) => ({
                        ...item.product,
                        quantity: item.quantity,
                        provideAfterTreatment: item.provideAfterTreatment,
                    }))}
                />
            )}
            {updatingService && (
                <TreatmentAddonUpdateDrawer
                    defaultValue={updatingService}
                    open={!!updatingService}
                    onClose={() => setupdatingService(null)}
                />
            )}
        </div>
    );
};

export default TreatmentDetailAddonServices;
